"use client";

import { useAtom } from "jotai";
import { atomWithReset, atomWithStorage } from "jotai/utils";
import { useState, useEffect, useRef, Dispatch, SetStateAction } from "react";
import { HiOutlineAdjustmentsHorizontal } from "react-icons/hi2";
import { PiCaretDownBold, PiChats, PiPaperclipLight } from "react-icons/pi";
import { useRouter } from "next/navigation";
import cn from "@/utils/class-names";
import Select from "@/components/ui/select";
import { useHover } from "@/hooks/use-hover";
import { useMedia } from "@/hooks/use-media";
import { Title } from "@/components/ui/text";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { ActionIcon } from "@/components/ui/action-icon";
import { getRelativeTime } from "@/utils/get-relative-time";
import rangeMap from "@/utils/range-map";
import { routes } from "@/config/routes";
import {
  messages,
  MessageType,
  supportStatuses,
  SupportStatusType,
  supportTypes,
} from "@/data/support-inbox";
import { LineGroup, Skeleton } from "@/components/ui/skeleton";
import SimpleBar from "@/components/ui/simplebar";
import { AnyObject, StateEveryType } from "@/types/global";
import { ChatRoomType } from "@/types/requests/ChatRoom";
import getData from "@/functions/getData";
import { Axios } from "axios";
import useFetch from "@/hooks/fetch/useFetch";
import { apis } from "@/static/apis/apis";
import Image from "next/image";
import avatarAddress from "@/functions/avatarAddress";
import { Avatar, Button } from "rizzui";
import postData from "@/functions/postData";
import useAxiosAuth from "@/hooks/fetch/useAxiosAuth";
import { produce } from "immer";
import DateCell from "@/components/ui/date-cell";
import { useSession } from "next-auth/react";
import putData from "@/functions/putData";

interface MessageItemProps {
  message: ChatRoomType;
  className?: string;
  id: number;
  setId: Dispatch<SetStateAction<number>>;
}

export const messageIdAtom = atomWithStorage("messageId", "");
export const dataAtom = atomWithReset<MessageType[]>(messages);

export function MessageItem({
  className,
  message: chatRoom,
  id,
  setId,
}: MessageItemProps) {
  const { data: session } = useSession();
  let admin = session?.user.user_permissions.chatroom_watcher;
  const hoverRef = useRef(null);
  const router = useRouter();
  const isHover = useHover(hoverRef);
  const [data, setData] = useAtom(dataAtom);
  const isMobile = useMedia("(max-width: 1023px)", false);
  const [messageId, setMessageId] = useState<number>(0);
  const isActive = id === chatRoom.id;
  const handleItemChange = (itemId: string) => {
    const updatedItems = data.map((item) =>
      item.id === itemId ? { ...item, selected: !item.selected } : item
    );
    setData(updatedItems);
  };
  const url = routes.support.messageDetails(`${messageId}`);
  return (
    <div
      ref={hoverRef}
      onClick={() => {
        setId(chatRoom.id);
      }}
      className={cn(
        className,
        "grid cursor-pointer grid-cols-[56px_1fr] items-start gap-3 border-t border-gray-200 p-5 relative",
        isActive && "border-t-2 border-t-primary dark:bg-gray-100/70"
      )}
    >
      <div className="flex items-center justify-between lg:flex-col lg:items-start 2xl:flex-row 2xl:items-center">
        <Avatar src={avatarAddress(0)} name="avatar" size="xl"></Avatar>
        <Title as="h4" className="flex items-center p-2">
          <span className="text-sm font-semibold dark:text-gray-700">
            {/* {chatRoom.owner.username} */}
            {admin ? chatRoom.owner.username : "ادمین"}
          </span>
        </Title>
        <span className="text-xs text-gray-500">
          {/* {chatRoom.create_date} */}
        </span>
      </div>
      <p className="mt-1 line-clamp-3 text-sm text-gray-500">{`#${chatRoom.id}`}</p>
      {/* <DateCell
        className="absolute bottom-0 left-3"
        date={new Date(chatRoom.create_date)}
      /> */}
      <div className="absolute bottom-0 left-3 text-gray-500" dir="rtl">
        {new Date(chatRoom.create_date).toLocaleDateString()}
      </div>
    </div>
  );
}

const sortOptions = {
  asc: "asc",
  desc: "desc",
} as const;

const options = [
  {
    value: sortOptions.asc,
    name: "قدیمی",
  },
  {
    value: sortOptions.desc,
    name: "جدید‌ها",
  },
];

const sortByDate = (items: MessageType[], order: SortByType) => {
  return items.slice().sort((a, b) => {
    const dateA = new Date(a.date).valueOf();
    const dateB = new Date(b.date).valueOf();

    if (order === "asc") {
      return dateA - dateB;
    } else {
      return dateB - dateA;
    }
  });
};

interface InboxListProps {
  className?: string;
  id: number;
  setId: Dispatch<SetStateAction<number>>;
}
type SortByType = keyof typeof sortOptions;

export default function MessageList({ className, id, setId }: InboxListProps) {
  const { data: session } = useSession();

  let admin = session?.user.user_permissions.chatroom_watcher;
  const [chatData, setChatData] = useFetch(
    (axios: Axios, setData: Dispatch<any>) =>
      getData(
        admin ? apis.chatroom.list : apis.chatroom.myList,
        axios,
        setData,
        false,
        undefined,
        undefined,
        {
          type: "admin_help",
        }
      )
  ) as [
    chatData: StateEveryType<ChatRoomType[]>,
    setChatData: Dispatch<SetStateAction<StateEveryType<ChatRoomType[]>>>,
  ];
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [sortBy, setSortBy] = useState<SortByType>(sortOptions.desc);
  const [status, setStatus] = useState<SupportStatusType>(supportStatuses.Open);
  const [selectAll, setSelectAll] = useState(false);
  const axios = useAxiosAuth();
  const [newRoom, setNewRoom] = useState<AnyObject>({
    data: undefined,
    loading: false,
    error: null,
  });
  useEffect(() => {
    if (
      chatData?.data?.length &&
      chatData.data?.filter((message) => message.type === "admin_help").length >
        0
    ) {
      setId(
        chatData.data?.filter((message) => message.type === "admin_help")[0].id
      );
    }
  }, [chatData]);
  return (
    <>
      <div className={cn(className, "sticky")}>
        <div className="mb-7 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              isLoading={newRoom.loading}
              size="sm"
              onClick={() => {
                postData(
                  apis.chatroom.create,
                  axios,
                  {
                    type: "admin_help",
                    bid: null,
                  },
                  setNewRoom,
                  true,
                  (res) => {
                    setChatData((x) =>
                      produce(x, (draftState) => {
                        if (draftState.data)
                          draftState.data = [...draftState.data, res.data];
                      })
                    );
                  }
                );
              }}
            >
              ساخت چت جدید
            </Button>
          </div>

          {/* <Select
            size="sm"
            variant="text"
            value={sortBy}
            options={options}
            getOptionValue={(option) => option.value}
            displayValue={(selected) =>
              options.find((o) => o.value === selected)?.name
            }
            suffix={<PiCaretDownBold className="w- ml-2 h-3.5 w-3.5" />}
            selectClassName="text-sm px-2.5 bf"
            optionClassName="text-sm"
            dropdownClassName="p-2 w-32 left-auto right-auto"
          /> */}
        </div>

        <div className=" rounded-lg border border-gray-200">
          <div className="max-h-[calc(100dvh-356px)] md:max-h-[calc(100dvh-311px)] lg:max-h-[calc(100dvh-240px)] xl:max-h-[calc(100dvh-230px)] 2xl:max-h-[calc(100dvh-240px)] 3xl:max-h-[calc(100dvh-270px)] overflow-auto scrollbar-thumb-gray-300 scrollbar-track-gray-0 h-[calc(100dvh-20rem)] scrollbar-thin">
            {chatData?.loading ? (
              <div className="grid gap-4">
                {rangeMap(5, (i) => (
                  <MessageLoader key={i} />
                ))}
              </div>
            ) : chatData.data?.filter(
                (message) => message.type === "admin_help"
              ).length === 0 ? (
              <div className="py-12 flex justify-center text-center items-center content-center h-full ">
                چتی وجود ندارد
              </div>
            ) : (
              chatData.data
                ?.filter((message) => message.type === "admin_help")
                .map((message: any) => (
                  <MessageItem
                    id={id}
                    setId={setId}
                    key={message.id}
                    message={message}
                  />
                ))
            )}
          </div>
        </div>
      </div>
      <div
        className={cn(
          "border-solid border rounded-md my-2 border-gray-200 p-5 dark:bg-gray-100/70 flex flex-row justify-between text-center items-center",
          id > -1 ? (admin ? "" : "hidden") : "hidden"
        )}
      >
        <p>پایان دادن به گفت‌وگو</p>
        <Button
          onClick={() =>
            putData(
              apis.chatroom.adminDone(id),
              axios,
              {},
              undefined,
              true,
              (res) => {
                setChatData((x: StateEveryType<ChatRoomType[]>) =>
                  produce(x, (draftData) => {
                    if (draftData.data)
                      draftData.data = draftData.data.filter(
                        (x) => x.id !== id
                      );
                  })
                );
                setId(-1);
              }
            )
          }
        >
          اتمام
        </Button>
      </div>
    </>
  );
}

export function MessageLoader({ className }: { className?: string }) {
  return (
    <div className={"grid gap-3 border-t border-gray-200 p-5 " + className}>
      <div className="flex items-center gap-2">
        <Skeleton className="h-6 w-6 rounded" />
        <Skeleton className="h-3 w-32 rounded" />
        <Skeleton className="h-3 w-3 rounded-full" />
        <Skeleton className="ml-auto h-3 w-16 rounded" />
      </div>
      <LineGroup
        columns={6}
        className="grid-cols-6 gap-1.5"
        skeletonClassName="h-2"
      />
      <LineGroup
        columns={5}
        className="grid-cols-5 gap-1.5"
        skeletonClassName="h-2"
      />
      <LineGroup
        columns={4}
        className="grid-cols-4 gap-1.5"
        skeletonClassName="h-2"
      />
    </div>
  );
}
